import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/esm/objectWithoutPropertiesLoose';
import _extends from '@babel/runtime/helpers/esm/extends';
import { createContext, useContext, useState, useEffect, forwardRef, createElement, __SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED } from 'react';
import { css, cx } from 'emotion';
import facepaint from 'facepaint';
var defaultTheme = {
  // mobile, desktop and large screens
  breakpoints: [768, 1280, 1920],
  // degrees of 2 except insignificant 2
  spaces: [0, 4, 8, 16, 32, 64, 128, 256]
};
var SystemContext = createContext(defaultTheme);

var makeQuery = function makeQuery(value) {
  var convertedValue = typeof value === "number" ? Math.ceil(value / 16) + "em" : value;
  return "screen and (min-width: " + convertedValue + ")";
};

var makeMediaRules = function makeMediaRules(queries) {
  return function (styles) {
    var result = _extends({}, styles[0]);

    styles.slice(1).forEach(function (style, index) {
      result[queries[index]] = style;
    });
    return result;
  };
};

var SystemProvider = SystemContext.Provider;

var makeMedia = function makeMedia(context) {
  var queries = context.breakpoints.map(function (bp) {
    return "@media " + makeQuery(bp);
  });
  var fp = facepaint(queries);
  var mr = makeMediaRules(queries);
  return function (styles) {
    if (Array.isArray(styles)) {
      return mr(styles);
    } else {
      return fp(styles);
    }
  };
};
/* system hook */


var useResponsive = function useResponsive() {
  var context = useContext(SystemContext);

  var _React$useState = useState(0),
      index = _React$useState[0],
      setIndex = _React$useState[1];

  useEffect(function () {
    var handleResize = function handleResize() {
      var currentIndex = 0;
      context.breakpoints.forEach(function (bp, i) {
        if (window.matchMedia(makeQuery(bp)).matches) {
          // one more for smallest value
          currentIndex = i + 1;
        }
      });
      setIndex(currentIndex);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return function () {
      window.removeEventListener("resize", handleResize);
    };
  }, [context]);

  var responsive = function responsive(values) {
    return values[Math.max(0, Math.min(index, values.length - 1))];
  };

  return responsive;
};

var useSystem = function useSystem() {
  var context = useContext(SystemContext);
  var media = makeMedia(context);

  var toSpace = function toSpace(value) {
    return Array.isArray(value) ? value.map(function (item) {
      return getSpace(item, context);
    }) : getSpace(value, context);
  };

  var pt = function pt(v) {
    return media({
      paddingTop: toSpace(v)
    });
  };

  var pr = function pr(v) {
    return media({
      paddingRight: toSpace(v)
    });
  };

  var pb = function pb(v) {
    return media({
      paddingBottom: toSpace(v)
    });
  };

  var pl = function pl(v) {
    return media({
      paddingLeft: toSpace(v)
    });
  };

  var px = function px(v) {
    return [pl(v), pr(v)];
  };

  var py = function py(v) {
    return [pt(v), pb(v)];
  };

  var p = function p(v) {
    return [pt(v), pr(v), pb(v), pl(v)];
  };

  var mt = function mt(v) {
    return media({
      marginTop: toSpace(v)
    });
  };

  var mr = function mr(v) {
    return media({
      marginRight: toSpace(v)
    });
  };

  var mb = function mb(v) {
    return media({
      marginBottom: toSpace(v)
    });
  };

  var ml = function ml(v) {
    return media({
      marginLeft: toSpace(v)
    });
  };

  var mx = function mx(v) {
    return [ml(v), mr(v)];
  };

  var my = function my(v) {
    return [mt(v), mb(v)];
  };

  var m = function m(v) {
    return [mt(v), mr(v), mb(v), ml(v)];
  };

  return {
    media: media,
    pt: pt,
    pr: pr,
    pb: pb,
    pl: pl,
    px: px,
    py: py,
    p: p,
    mt: mt,
    mr: mr,
    mb: mb,
    ml: ml,
    mx: mx,
    my: my,
    m: m
  };
};
/* deprecated media utility*/


var _SECRET_INTERNALS_DO = __SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED,
    ReactCurrentDispatcher = _SECRET_INTERNALS_DO.ReactCurrentDispatcher,
    ReactCurrentOwner = _SECRET_INTERNALS_DO.ReactCurrentOwner;

function readContext(Context) {
  var dispatcher = ReactCurrentDispatcher != null ? ReactCurrentDispatcher.current : ReactCurrentOwner.currentDispatcher;
  return dispatcher.readContext(Context);
}

var media = function media(styles) {
  var context = readContext(SystemContext);
  var media = makeMedia(context);
  return media(styles);
};
/* Flex/Box */


var id2 = function id2(first, second) {
  return first;
};

var makePercent = function makePercent(value) {
  return value === 0 ? 0 : value * 100 + "%";
};

var getSizeValue = function getSizeValue(value) {
  return typeof value === "number" ? makePercent(Math.max(0, Math.min(value, 1))) : value;
}; // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/sign#Polyfill


var sign = function sign(x) {
  // If x is NaN, the result is NaN.
  // If x is -0, the result is -0.
  // If x is +0, the result is +0.
  // If x is negative and not -0, the result is -1.
  // If x is positive and not +0, the result is +1.
  return Number(x > 0) - Number(x < 0) || +x; // A more aesthetical persuado-representation is shown below
  //
  // ( (x > 0) ? 0 : 1 )  // if x is negative then negative one
  //          +           // else (because you cant be both - and +)
  // ( (x < 0) ? 0 : -1 ) // if x is positive then positive one
  //         ||           // if x is 0, -0, or NaN, or not a number,
  //         +x           // Then the result will be x, (or) if x is
  //                      // not a number, then x converts to number
};

var getSpace = function getSpace(value, _ref) {
  var spaces = _ref.spaces;

  if (typeof value === "number") {
    var max = spaces.length - 1;
    var bound = Math.max(-max, Math.min(value, max));
    return sign(bound) * spaces[Math.abs(bound)];
  } else {
    return value;
  }
};

var sizeStyles = [{
  prop: "width",
  transform: getSizeValue
}, {
  prop: "height",
  transform: getSizeValue
}];
var spaceStyles = [{
  prop: "p",
  cssProp: "paddingTop",
  transform: getSpace
}, {
  prop: "p",
  cssProp: "paddingRight",
  transform: getSpace
}, {
  prop: "p",
  cssProp: "paddingBottom",
  transform: getSpace
}, {
  prop: "p",
  cssProp: "paddingLeft",
  transform: getSpace
}, {
  prop: "ph",
  cssProp: "paddingLeft",
  transform: getSpace
}, {
  prop: "ph",
  cssProp: "paddingRight",
  transform: getSpace
}, {
  prop: "px",
  cssProp: "paddingLeft",
  transform: getSpace
}, {
  prop: "px",
  cssProp: "paddingRight",
  transform: getSpace
}, {
  prop: "pv",
  cssProp: "paddingTop",
  transform: getSpace
}, {
  prop: "pv",
  cssProp: "paddingBottom",
  transform: getSpace
}, {
  prop: "py",
  cssProp: "paddingTop",
  transform: getSpace
}, {
  prop: "py",
  cssProp: "paddingBottom",
  transform: getSpace
}, {
  prop: "pt",
  cssProp: "paddingTop",
  transform: getSpace
}, {
  prop: "pr",
  cssProp: "paddingRight",
  transform: getSpace
}, {
  prop: "pb",
  cssProp: "paddingBottom",
  transform: getSpace
}, {
  prop: "pl",
  cssProp: "paddingLeft",
  transform: getSpace
}, {
  prop: "m",
  cssProp: "marginTop",
  transform: getSpace
}, {
  prop: "m",
  cssProp: "marginRight",
  transform: getSpace
}, {
  prop: "m",
  cssProp: "marginBottom",
  transform: getSpace
}, {
  prop: "m",
  cssProp: "marginLeft",
  transform: getSpace
}, {
  prop: "mh",
  cssProp: "marginLeft",
  transform: getSpace
}, {
  prop: "mh",
  cssProp: "marginRight",
  transform: getSpace
}, {
  prop: "mx",
  cssProp: "marginLeft",
  transform: getSpace
}, {
  prop: "mx",
  cssProp: "marginRight",
  transform: getSpace
}, {
  prop: "mv",
  cssProp: "marginTop",
  transform: getSpace
}, {
  prop: "mv",
  cssProp: "marginBottom",
  transform: getSpace
}, {
  prop: "my",
  cssProp: "marginTop",
  transform: getSpace
}, {
  prop: "my",
  cssProp: "marginBottom",
  transform: getSpace
}, {
  prop: "mt",
  cssProp: "marginTop",
  transform: getSpace
}, {
  prop: "mr",
  cssProp: "marginRight",
  transform: getSpace
}, {
  prop: "mb",
  cssProp: "marginBottom",
  transform: getSpace
}, {
  prop: "ml",
  cssProp: "marginLeft",
  transform: getSpace
}];
var flexItemStyles = [{
  prop: "flexGrow"
}, {
  prop: "flexShrink"
}, {
  prop: "flexBasis"
}, {
  prop: "justifySelf"
}, {
  prop: "alignSelf"
}, {
  prop: "order"
}];
var flexBoxStyles = [{
  prop: "alignItems"
}, {
  prop: "alignContent"
}, {
  prop: "justifyItems"
}, {
  prop: "justifyContent"
}, {
  prop: "flexWrap"
}, {
  prop: "flexDirection"
}];

var omit = function omit(obj, blacklist) {
  var next = {};

  for (var key in obj) {
    if (blacklist.indexOf(key) === -1) {
      next[key] = obj[key];
    }
  }

  return next;
};

var getStylePropName = function getStylePropName(style) {
  return style.prop;
};

var transformValues = function transformValues(props, context, styles) {
  var generated = {};

  var _loop = function _loop(i) {
    var _styles$i = styles[i],
        prop = _styles$i.prop,
        _styles$i$cssProp = _styles$i.cssProp,
        cssProp = _styles$i$cssProp === void 0 ? prop : _styles$i$cssProp,
        _styles$i$transform = _styles$i.transform,
        transform = _styles$i$transform === void 0 ? id2 : _styles$i$transform;
    var value = props[prop];

    if (value != null) {
      generated[cssProp] = Array.isArray(value) ? value.map(function (item) {
        return transform(item, context);
      }) : transform(value, context);
    }
  };

  for (var i = 0; i < styles.length; i += 1) {
    _loop(i);
  }

  return generated;
};

var initialBoxStyle = css({
  boxSizing: "border-box",
  minWidth: 0,
  minHeight: 0
});
var Box = forwardRef(function (_ref2, ref) {
  var _ref2$as = _ref2.as,
      as = _ref2$as === void 0 ? "div" : _ref2$as,
      _ref2$className = _ref2.className,
      className = _ref2$className === void 0 ? "" : _ref2$className,
      cssProp = _ref2.css,
      children = _ref2.children,
      props = _objectWithoutPropertiesLoose(_ref2, ["as", "className", "css", "children"]);

  var context = useContext(SystemContext);

  var _useSystem = useSystem(),
      media = _useSystem.media;

  var styles = sizeStyles.concat(spaceStyles, flexItemStyles);
  var generated = transformValues(props, context, styles);
  var generatedClassName = css(cssProp, media(generated));
  var rest = omit(props, styles.map(getStylePropName));
  return createElement(as, _extends({
    ref: ref,
    className: cx(initialBoxStyle, className, generatedClassName)
  }, rest), children == null ? null : children);
});
Box.displayName = "Box";
var initialFlexStyle = css({
  display: "flex",
  boxSizing: "border-box",
  minWidth: 0,
  minHeight: 0
});
var Flex = forwardRef(function (_ref3, ref) {
  var _ref3$as = _ref3.as,
      as = _ref3$as === void 0 ? "div" : _ref3$as,
      _ref3$className = _ref3.className,
      className = _ref3$className === void 0 ? "" : _ref3$className,
      cssProp = _ref3.css,
      children = _ref3.children,
      props = _objectWithoutPropertiesLoose(_ref3, ["as", "className", "css", "children"]);

  var context = useContext(SystemContext);

  var _useSystem2 = useSystem(),
      media = _useSystem2.media;

  var styles = sizeStyles.concat(spaceStyles, flexItemStyles, flexBoxStyles);
  var generated = transformValues(props, context, styles);
  var generatedClassName = css(cssProp, media(generated));
  var rest = omit(props, styles.map(getStylePropName));
  return createElement(as, _extends({
    ref: ref,
    className: cx(initialFlexStyle, className, generatedClassName)
  }, rest), children == null ? null : children);
});
Flex.displayName = "Flex";
export { Box, Flex, SystemProvider, media, useResponsive, useSystem };